import React, { createContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline,
  Typography,
  Button,
  Card,
  Chip,
  Box
} from '@mui/material';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CategoriesPage from './pages/CategoriesPage';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import UsersPage from './pages/UsersPage';
import FavoritesPage from './pages/FavoritesPage';
import Footer from './components/Footer';
import StatusBanner from './components/StatusBanner';
import { createTheme as createThemeMui } from '@mui/material/styles';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Create a theme instance with dark green and off-white
const theme = createThemeMui({
  palette: {
    primary: {
      light: '#4c8c4a',
      main: '#2e7d32',
      dark: '#1b5e20',
      contrastText: '#fff',
    },
    secondary: {
      light: '#76d275',
      main: '#689f38',
      dark: '#387002',
      contrastText: '#fff',
    },
    background: {
      default: '#f8f8f5', // Off-white
      paper: '#ffffff',
    },
    text: {
      primary: '#263238',
      secondary: '#546e7a',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
    },
    h2: {
      fontWeight: 500,
    },
    h3: {
      fontWeight: 500,
    },
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1b5e20',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filledPrimary: {
          backgroundColor: '#2e7d32',
        },
      },
    },
  },
});

// Create a store context to hold the store name
export const StoreContext = createContext({
  name: 'Natural Source',
  tagline: 'Healthy snacks for a healthier you',
  description: 'Natural Source offers a wide variety of organic and natural snacks and beverages.',
  primaryColor: '#2e7d32'
});

// Authentication redirect component
const AuthRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Don't redirect during loading
  if (loading) return null;

  // If user is authenticated and at login/register page, redirect to home
  if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register')) {
    return <Navigate to="/" replace />;
  }

  // No redirect needed
  return null;
};

// Global authentication check component
const AuthCheck = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect during loading
    if (loading) return;

    // If user is not authenticated and not at login/register page, redirect to login
    if (!isAuthenticated && 
        location.pathname !== '/login' && 
        location.pathname !== '/register') {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, loading, location.pathname, navigate]);

  return null;
};

const App: React.FC = () => {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <CartProvider>
            <StoreContext.Provider value={{
              name: 'Natural Source',
              tagline: 'Healthy snacks for a healthier you',
              description: 'Natural Source offers a wide variety of organic and natural snacks and beverages.',
              primaryColor: '#2e7d32'
            }}>
              <BrowserRouter>
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <Header />
                  <StatusBanner />
                  <AuthCheck />
                  <AuthRedirect />
                  <Box component="main" sx={{ flexGrow: 1, pb: 6 }}>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/product/:id" element={<ProductPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/categories" element={<CategoriesPage />} />
                      <Route path="/category/:id" element={<HomePage />} />
                      <Route path="/search" element={<HomePage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/favorites" element={<FavoritesPage />} />
                      <Route path="/orders" element={<OrdersPage />} />
                      <Route path="/orders/:orderId" element={<OrderDetailsPage />} />
                      <Route path="/admin/users" element={<UsersPage />} />
                      {/* 404 catch-all route */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Box>
                  <Footer />
                </div>
              </BrowserRouter>
              <Toaster position="bottom-center" />
            </StoreContext.Provider>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
